export const FEEDBACK_CATEGORIES = [
  'bug',
  'idea',
  'confusing',
  'performance',
  'other',
] as const;

export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number];

export const FEEDBACK_STATUSES = ['new', 'reviewed', 'archived'] as const;

export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

export const FEEDBACK_PLATFORMS = ['ios', 'android', 'web', 'other'] as const;

export type FeedbackPlatform = (typeof FEEDBACK_PLATFORMS)[number];

export type FeedbackMetadataValue = string | number | boolean | null;

export interface OnboardingPreviewFeedbackInput {
  /** Must match the authenticated account, including when replaying an offline vote. */
  accountId: string;
  sentiment: 'up' | 'down';
  /** `web` is accepted since the web CV-completion dialog asks the same question. */
  platform: FeedbackPlatform;
  locale: 'en' | 'ar';
  appVersion?: string;
  /** "Needs work" issue chip. Only valid with `sentiment: 'down'`. */
  reason?: 'details' | 'wording' | 'layout' | 'other';
  /** Optional private note (trimmed, <= 1,000 chars). Only valid with `sentiment: 'down'`. */
  message?: string;
  /** Stable per unchanged payload so a retry reuses the same request. <= 128 chars. */
  idempotencyKey?: string;
}

export interface OnboardingPreviewFeedbackReceipt {
  id: string;
  recorded: true;
  /** Receipt fields (subscription-journeys L1b). "Feedback sent" requires all three. */
  status: 'received';
  feedbackId: string;
  receivedAt: string;
}

export interface CreateFeedbackSubmissionInput {
  category: FeedbackCategory;
  rating: number;
  message: string;
  route?: string;
  platform: FeedbackPlatform;
  appVersion?: string;
  locale?: string;
  /** Optional public URL of a screenshot uploaded to DigitalOcean Spaces. */
  screenshotUrl?: string;
  metadata?: Record<string, FeedbackMetadataValue>;
}

export interface UserFeedbackSubmissionSummary {
  id: string;
  userId: string;
  category: FeedbackCategory;
  rating: number;
  message: string;
  route: string | null;
  platform: FeedbackPlatform;
  appVersion: string | null;
  locale: string | null;
  status: FeedbackStatus;
  createdAt: string;
  updatedAt: string;
}
